import { useCallback, useState } from "react";
import { useService } from "./useService";
import type {
  AddressFieldsPayload,
  AddressVerificationResult,
} from "@/types/Loqate";

export interface AddressIssue {
  /** Loqate's corrected address, or null when none was offered. */
  suggestion: AddressFieldsPayload | null;
}

/**
 * Non-blocking Loqate address check. Call `checkAddress` when a manual/edited
 * address is complete; an `issue` is raised only when Loqate reports the address
 * as unverified. "unchecked" (API error) and "verified" raise nothing. Returns
 * the raw result so callers can record the verification status for submission.
 */
export function useAddressVerification() {
  const service = useService();
  const [issue, setIssue] = useState<AddressIssue | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkAddress = useCallback(
    async (
      address: AddressFieldsPayload,
    ): Promise<AddressVerificationResult | null> => {
      setIsChecking(true);
      try {
        const result = await service.verifyAddress(address);
        setIssue(
          result.status === "unverified"
            ? { suggestion: result.suggestion }
            : null,
        );
        return result;
      } catch {
        setIssue(null);
        return null;
      } finally {
        setIsChecking(false);
      }
    },
    [service],
  );

  const clearIssue = useCallback(() => setIssue(null), []);

  return { issue, isChecking, checkAddress, clearIssue };
}
